// Feb 2026 · R280 · Public booking tracker
// Customers open this page from the "Track this booking →" CTA in every
// transactional email. Auth-free — email query param + ref_no in the
// URL act as the shared secret. Renders:
//   • Status timeline card (received → confirmed → today → completed)
//   • Slot, space, studio contact card
//   • Payment summary (paid, balance, deposit)
//   • Discussion feed (customer-visible admin comments + system updates)
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, MapPin, Phone, MessageCircle, ExternalLink, CircleCheck, CircleAlert, Clock, RefreshCw, QrCode, IndianRupee, CalendarClock } from "lucide-react";
import { RichTextDisplay } from "@/components/RichTextEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const STATUS_STEPS = [
  { key: "pending_payment",      label: "Booking received",  hint: "Awaiting payment" },
  { key: "pending_verification", label: "Payment submitted", hint: "Verifying UPI transfer" },
  { key: "confirmed",            label: "Confirmed",         hint: "See you at your slot" },
  { key: "completed",            label: "Completed",         hint: "Thanks for shooting with us" },
];

function formatSlot(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch (_e) { return iso; }
}

function relative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.round((now - then) / 60000);
  if (diff < 1) return "just now";
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.round(diff / 60)}h ago`;
  return `${Math.round(diff / 1440)}d ago`;
}

export default function StudioBookingTracker() {
  const { wsId, ref } = useParams();
  const [sp] = useSearchParams();
  const email = sp.get("email") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const url = `${API}/public/studio/track/${wsId}/${ref}?email=${encodeURIComponent(email)}`;
      const { data: r } = await axios.get(url);
      setData(r);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Unable to load booking. Double-check the link from your email.";
      setError(typeof detail === "string" ? detail : "Unable to load booking");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, ref, email]);

  const step = useMemo(() => {
    if (!data?.booking) return -1;
    if (data.booking.status === "cancelled") return -2;
    const idx = STATUS_STEPS.findIndex((s) => s.key === data.booking.status);
    return idx < 0 ? 0 : idx;
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF7F0] flex flex-col items-center justify-center text-[#241A11]">
        <Loader2 className="w-6 h-6 animate-spin" />
        <div className="text-xs uppercase tracking-[3px] mt-3">Loading booking…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#FBF7F0] flex flex-col items-center justify-center p-6 text-[#241A11]">
        <CircleAlert className="w-8 h-8 text-rose-600 mb-3" />
        <h1 className="text-xl font-semibold" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>Can't open this booking</h1>
        <p className="text-sm text-[#5C4A38] mt-2 text-center max-w-md" data-testid="tracker-error">{error}</p>
        <p className="text-xs text-[#8A7A66] mt-4 text-center max-w-md">Open the exact link from your booking email — the address in the URL has to match the one on record.</p>
      </div>
    );
  }

  const b = data.booking;
  const studio = data.studio || {};
  const quote = data.quote || {};
  const isCancelled = b.status === "cancelled";
  const badge = isCancelled
    ? { text: "Cancelled", cls: "bg-rose-100 text-rose-800 border-rose-300" }
    : b.status === "confirmed"
      ? { text: "Confirmed",  cls: "bg-emerald-100 text-emerald-800 border-emerald-300" }
      : b.status === "completed"
        ? { text: "Completed", cls: "bg-neutral-200 text-neutral-800 border-neutral-400" }
        : b.status === "pending_verification"
          ? { text: "Verifying payment", cls: "bg-amber-100 text-amber-800 border-amber-300" }
          : { text: "Received", cls: "bg-blue-100 text-blue-800 border-blue-300" };

  return (
    <div className="min-h-screen bg-[#FBF7F0] text-[#241A11] py-10 px-4" data-testid="tracker-page"
          style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>
      <div className="max-w-2xl mx-auto space-y-5">
        {/* Header */}
        <div className="text-center">
          {studio.logo_url && (
            // eslint-disable-next-line
            <img src={studio.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${studio.logo_url}` : studio.logo_url}
                  alt={studio.name} className="mx-auto max-h-14 mb-3" />
          )}
          <div className="text-[10px] tracking-[3px] uppercase text-[#5C4A38] font-semibold">{studio.name || "Studio Booking"}</div>
          <h1 className="text-4xl md:text-5xl font-medium mt-1" style={{ letterSpacing: "-0.02em" }}>Booking tracker</h1>
          <div className="text-sm text-[#5C4A38] mt-1 font-mono">#{b.ref_no || b.code}</div>
        </div>

        {/* Status card */}
        <div className="bg-white border border-[#F3ECE0] p-6">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 border text-[11px] uppercase tracking-widest font-semibold ${badge.cls}`}
                     data-testid="tracker-status-badge">
              <CircleCheck className="w-3.5 h-3.5" /> {badge.text}
            </span>
            <button onClick={load} className="ml-auto text-[11px] uppercase tracking-widest text-[#5C4A38] hover:text-[#241A11] flex items-center gap-1"
                       data-testid="tracker-refresh">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>

          {/* Timeline */}
          {!isCancelled && (
            <div className="mt-6 grid grid-cols-4 gap-2">
              {STATUS_STEPS.map((s, i) => {
                const active = i <= step;
                const now = i === step;
                return (
                  <div key={s.key} className={`text-center pt-3 border-t-4 ${active ? "border-[#B8860B]" : "border-[#F3ECE0]"}`}>
                    <div className={`text-[10px] uppercase tracking-widest font-semibold ${active ? "text-[#241A11]" : "text-[#8A7A66]"}`}>{s.label}</div>
                    <div className={`text-[10px] mt-0.5 ${now ? "text-[#B8860B]" : "text-[#8A7A66]"}`}>{now ? s.hint : (active ? "Done" : "—")}</div>
                  </div>
                );
              })}
            </div>
          )}
          {isCancelled && b.cancel_reason && (
            <div className="mt-4 border border-rose-200 bg-rose-50/60 p-3 text-xs">
              <div className="uppercase tracking-widest font-semibold text-rose-700 text-[10px] mb-1">Cancellation note</div>
              {b.cancel_reason}
              {b.refund_amount != null && (
                <div className="mt-1 text-[11px]">Refund of ₹{Number(b.refund_amount).toLocaleString("en-IN")} is being processed.</div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6 mt-6 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8A7A66]">Slot</div>
              <div className="font-semibold" data-testid="tracker-slot">{formatSlot(b.slot_start)}</div>
              <div className="text-xs text-[#5C4A38]">→ {formatSlot(b.slot_end)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8A7A66]">Space</div>
              <div className="font-semibold">{data.space?.name || (b.is_offsite ? "Off-site shoot" : "—")}</div>
              {b.location_notes && <div className="text-xs text-[#5C4A38]">{b.location_notes}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8A7A66]">Guest</div>
              <div className="font-semibold">{b.customer_name || "—"}</div>
              <div className="text-xs text-[#5C4A38]">{b.customer_email}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8A7A66]">Total</div>
              <div className="font-semibold">₹{Number(quote.total || 0).toLocaleString("en-IN")}</div>
              {quote.balance > 0 && (
                <div className="text-xs text-amber-700">Balance ₹{Number(quote.balance).toLocaleString("en-IN")}</div>
              )}
              {quote.balance === 0 && quote.total > 0 && (
                <div className="text-xs text-emerald-700">Paid in full</div>
              )}
            </div>
            {quote.damage_deposit > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#8A7A66]">Refundable deposit</div>
                <div className="font-semibold">₹{Number(quote.damage_deposit).toLocaleString("en-IN")}</div>
                {b.deposit_released_at && (
                  <div className="text-xs text-emerald-700">Refund ₹{Number(b.deposit_refund_amount || 0).toLocaleString("en-IN")} released {relative(b.deposit_released_at)}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions row · Feb 2026 R280.2 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <QrTicketCard wsId={wsId} refCode={ref} email={email} />
          <PayBalanceCard wsId={wsId} refCode={ref} email={email} balance={quote.balance} onPaid={load} />
          <RescheduleCard wsId={wsId} refCode={ref} email={email} slotStart={b.slot_start} slotEnd={b.slot_end} onDone={load} />
        </div>

        {/* Studio contact */}
        {(studio.phone || studio.whatsapp || studio.website) && (
          <div className="bg-white border border-[#F3ECE0] p-4 flex flex-wrap gap-3 items-center justify-center text-[13px]">
            {studio.phone && (
              <a href={`tel:${studio.phone}`} className="inline-flex items-center gap-1.5 text-[#B8860B] no-underline">
                <Phone className="w-3.5 h-3.5" /> {studio.phone}
              </a>
            )}
            {studio.whatsapp && (
              <a href={`https://wa.me/${(studio.whatsapp || "").replace(/\D+/g, "")}`}
                  target="_blank" rel="noreferrer noopener"
                  className="inline-flex items-center gap-1.5 text-emerald-600 no-underline">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </a>
            )}
            {studio.website && (
              <a href={studio.website} target="_blank" rel="noreferrer noopener"
                  className="inline-flex items-center gap-1.5 text-[#B8860B] no-underline">
                <ExternalLink className="w-3.5 h-3.5" /> Visit website
              </a>
            )}
          </div>
        )}

        {/* Updates feed */}
        <div className="bg-white border border-[#F3ECE0] p-6" data-testid="tracker-updates">
          <div className="text-[10px] uppercase tracking-[3px] font-semibold text-[#5C4A38] mb-3 flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> Updates from the studio
          </div>
          {(data.updates || []).length === 0 ? (
            <div className="text-xs text-[#8A7A66] italic">No updates yet — you'll see any admin notes about your booking here.</div>
          ) : (
            <div className="space-y-3">
              {data.updates.map((u) => {
                const b = String(u.body || "");
                // Feb 2026 · R280.4 · Comments from CommentThread's
                // RichTextEditor arrive as HTML — render via the
                // sanitised DOMPurify display. Plain-text notes (e.g.
                // customer-typed reschedule requests) get their
                // newlines converted to <br/> so they don't render as
                // a single squished line.
                const isHtml = /<\/?[a-z][\s\S]*>/i.test(b);
                const html = isHtml
                  ? b
                  : b.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
                return (
                  <div key={u.id} className="border-l-2 border-[#B8860B] pl-3 py-1">
                    <div className="text-xs text-[#5C4A38]">
                      <b>{u.author_name || "Studio team"}</b> · {relative(u.created_at)}
                    </div>
                    <div className="mt-0.5 text-sm booking-tracker-update">
                      <RichTextDisplay html={html} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-center text-[10px] uppercase tracking-[3px] text-[#8A7A66] pt-4">
          Powered by Acquilix<sup>™</sup>
        </div>
      </div>
    </div>
  );
}


// ─── Feb 2026 · R280.2 · QR ticket ──────────────────────────────────
function QrTicketCard({ wsId, refCode, email }) {
  const [open, setOpen] = useState(false);
  const qrUrl = `${API}/public/studio/track/${wsId}/${refCode}/qr.png?email=${encodeURIComponent(email)}`;
  return (
    <div className="bg-white border border-[#F3ECE0] p-4" data-testid="tracker-qr-card">
      <div className="flex items-center gap-2 mb-2">
        <QrCode className="w-4 h-4 text-[#B8860B]" />
        <div className="text-[10px] uppercase tracking-[3px] font-semibold">Arrival QR</div>
      </div>
      {open ? (
        <div className="text-center">
          {/* eslint-disable-next-line */}
          <img src={qrUrl} alt="Booking QR" className="mx-auto w-40 h-40 border border-[#F3ECE0]" data-testid="tracker-qr-image" />
          <div className="text-[10px] text-[#8A7A66] mt-2">Show this at the studio's front desk on arrival.</div>
          <div className="flex gap-2 justify-center mt-2">
            <a href={qrUrl} target="_blank" rel="noreferrer noopener"
                className="text-[10px] uppercase tracking-widest text-[#B8860B] hover:underline">
              Download
            </a>
            <button onClick={() => setOpen(false)}
                    className="text-[10px] uppercase tracking-widest text-[#8A7A66] hover:text-[#241A11]">
              Hide
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-xs text-[#5C4A38] mb-2">A scannable ticket the front desk can check on arrival — no app needed.</p>
          <button onClick={() => setOpen(true)}
                    data-testid="tracker-qr-show"
                    className="w-full bg-[#241A11] text-[#FBF7F0] text-[11px] uppercase tracking-widest font-semibold py-2 border border-[#241A11] hover:opacity-90">
            Show QR ticket
          </button>
        </>
      )}
    </div>
  );
}


// ─── Feb 2026 · R280.2 · Pay outstanding balance ────────────────────
function PayBalanceCard({ wsId, refCode, email, balance, onPaid }) {
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState(null);
  const [error, setError] = useState("");
  const hasBalance = Number(balance || 0) > 0;
  const start = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(
        `${API}/public/studio/track/${wsId}/${refCode}/pay?email=${encodeURIComponent(email)}`
      );
      if (data.paid_in_full) {
        setError("This booking is already paid in full.");
        if (onPaid) onPaid();
        return;
      }
      setPay(data);
      // Cashfree hosted-checkout → open the redirect URL in a new tab.
      const url = data.payment_session_url || data.checkout_url || data.hosted_url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e?.response?.data?.detail || "Payment could not be started. Please contact the studio.");
    } finally {
      setBusy(false);
    }
  };
  if (!hasBalance) {
    return (
      <div className="bg-white border border-[#F3ECE0] p-4" data-testid="tracker-pay-card">
        <div className="flex items-center gap-2 mb-2">
          <IndianRupee className="w-4 h-4 text-emerald-600" />
          <div className="text-[10px] uppercase tracking-[3px] font-semibold">Payment</div>
        </div>
        <p className="text-xs text-emerald-700">Paid in full — nothing outstanding.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#F3ECE0] p-4" data-testid="tracker-pay-card">
      <div className="flex items-center gap-2 mb-2">
        <IndianRupee className="w-4 h-4 text-[#B8860B]" />
        <div className="text-[10px] uppercase tracking-[3px] font-semibold">Balance due</div>
      </div>
      <p className="text-xs text-[#5C4A38] mb-2">₹{Number(balance).toLocaleString("en-IN")} outstanding — clear in one tap.</p>
      {pay?.provider === "upi_qr" ? (
        <div className="border border-[#F3ECE0] p-3 text-center">
          {pay.qr_url && (
            // eslint-disable-next-line
            <img src={pay.qr_url} alt="UPI QR" className="mx-auto w-40 h-40 border border-[#F3ECE0]" />
          )}
          <div className="text-[10px] text-[#5C4A38] mt-2 break-all">{pay.upi_id}</div>
          {pay.upi_deep_link && (
            <a href={pay.upi_deep_link}
                className="inline-block mt-2 bg-[#241A11] text-[#FBF7F0] text-[10px] uppercase tracking-widest px-3 py-1.5 no-underline">
              Open UPI app
            </a>
          )}
          <div className="text-[10px] text-[#8A7A66] mt-2">Send ₹{Number(pay.amount || balance).toLocaleString("en-IN")} and we'll auto-verify.</div>
        </div>
      ) : (
        <button onClick={start} disabled={busy}
                  data-testid="tracker-pay-btn"
                  className="w-full bg-[#B8860B] text-white text-[11px] uppercase tracking-widest font-semibold py-2 border border-[#B8860B] hover:opacity-90 disabled:opacity-60">
          {busy ? "Starting…" : `Pay ₹${Number(balance).toLocaleString("en-IN")} now`}
        </button>
      )}
      {pay?.provider === "offline" && (
        <div className="text-[10px] text-[#8A7A66] mt-2">{pay.message}</div>
      )}
      {error && <div className="text-[10px] text-rose-600 mt-2">{error}</div>}
    </div>
  );
}


// ─── Feb 2026 · R280.2 · Reschedule request ─────────────────────────
function RescheduleCard({ wsId, refCode, email, slotStart, slotEnd, onDone }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e?.preventDefault();
    setError(""); setMsg("");
    if (!start) { setError("Please pick a preferred start time."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/public/studio/track/${wsId}/${refCode}/reschedule-request`,
        { email, preferred_start: start, preferred_end: end, note }
      );
      setMsg(data.acknowledgement || "Thanks — your request has been sent.");
      setStart(""); setEnd(""); setNote("");
      if (onDone) setTimeout(onDone, 1200);
    } catch (er) {
      setError(er?.response?.data?.detail || "Couldn't send your request. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bg-white border border-[#F3ECE0] p-4" data-testid="tracker-reschedule-card">
      <div className="flex items-center gap-2 mb-2">
        <CalendarClock className="w-4 h-4 text-[#B8860B]" />
        <div className="text-[10px] uppercase tracking-[3px] font-semibold">Request reschedule</div>
      </div>
      {!open ? (
        <>
          <p className="text-xs text-[#5C4A38] mb-2">Need a different slot? Send our team a preferred time — we'll confirm shortly.</p>
          <button onClick={() => setOpen(true)}
                    data-testid="tracker-reschedule-open"
                    className="w-full border border-[#241A11] text-[#241A11] text-[11px] uppercase tracking-widest font-semibold py-2 hover:bg-[#241A11] hover:text-[#FBF7F0]">
            Suggest a new slot
          </button>
          {msg && <div className="text-[10px] text-emerald-700 mt-2">{msg}</div>}
        </>
      ) : (
        <form onSubmit={submit} className="space-y-2">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#8A7A66]">Preferred start</label>
            <input type="datetime-local" required value={start} onChange={(ev) => setStart(ev.target.value)}
                     data-testid="tracker-reschedule-start"
                     className="w-full border border-[#F3ECE0] text-xs px-2 py-1.5 font-mono" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#8A7A66]">Preferred end (optional)</label>
            <input type="datetime-local" value={end} onChange={(ev) => setEnd(ev.target.value)}
                     data-testid="tracker-reschedule-end"
                     className="w-full border border-[#F3ECE0] text-xs px-2 py-1.5 font-mono" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#8A7A66]">Note (optional)</label>
            <textarea rows={2} value={note} onChange={(ev) => setNote(ev.target.value)}
                          data-testid="tracker-reschedule-note"
                          placeholder="Any context for our team…"
                          className="w-full border border-[#F3ECE0] text-xs px-2 py-1.5" />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={busy}
                    data-testid="tracker-reschedule-submit"
                    className="flex-1 bg-[#241A11] text-[#FBF7F0] text-[11px] uppercase tracking-widest font-semibold py-1.5 disabled:opacity-60">
              {busy ? "Sending…" : "Send request"}
            </button>
            <button type="button" onClick={() => setOpen(false)}
                    className="border border-[#F3ECE0] text-[#5C4A38] text-[11px] uppercase tracking-widest font-semibold py-1.5 px-3 hover:bg-[#FBF7F0]">
              Cancel
            </button>
          </div>
          {error && <div className="text-[10px] text-rose-600">{error}</div>}
          {msg && <div className="text-[10px] text-emerald-700">{msg}</div>}
          <div className="text-[10px] text-[#8A7A66]">Current: {(slotStart || "").replace("T", " ").slice(0, 16)} → {(slotEnd || "").replace("T", " ").slice(0, 16)}</div>
        </form>
      )}
    </div>
  );
}

